import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { IHomePage } from 'pages/home/home-interface';
import { ILoginPage } from 'pages/login/login-interface';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthService } from 'services/auth.service';
import { ReferralService } from 'services/referral.service';
import { ReferralInvitePageParams } from './referral-invite-interface';

/** What the page is showing right now. */
export enum ReferralInviteState {
  /** Asking the server who sent this invite. */
  Checking = 'checking',
  /** The invite resolved, and we know the inviter's first name. */
  Valid = 'valid',
  /** No invite, an invite we do not recognise, or the programme is off for that inviter. */
  Invalid = 'invalid',
  /** The server would not look the invite up: too many lookups from this address in the last hour (#7027). */
  TooManyAttempts = 'tooManyAttempts',
}

/**
 * The page a friend lands on after tapping somebody's invite link (#4627).
 *
 * This is the ONLY page in the app whose whole audience is people who have never
 * used it and are not signed in. Two consequences shape everything below.
 *
 * **It must render for a signed-out visitor**, which takes more than registering
 * the page. `AppComponent.pageFilterAsync` sends a visitor who is not signed in
 * to the login page for every page but the few it names by hand, so this page's
 * name had to be added to that list. Registering it in `app-page-list.ts` alone
 * is NOT enough, and nothing warns you — the visitor simply lands on the login
 * screen as if the link were ordinary.
 *
 * **It must never show an error screen.** A failed call to the server is
 * reported as "this invite is not valid", because a stranger's first impression
 * of us should not be a crash. `ReferralService.peekInviteAsync` does that
 * conversion, and still reports the failure to telemetry so that a broken
 * endpoint does not look like a run of bad links.
 *
 * Phase 1 grants nothing. The page says a free month is coming because phase 2
 * is what delivers it, which is why the flag must not be switched on for
 * everybody until then. See design-docs/referral-program.md.
 */
@Component({
  selector: 'page-referral-invite',
  templateUrl: 'referral-invite.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralInvitePage extends CsPage<ReferralInvitePageParams> {
  protected LL = LL;
  protected ReferralInviteState = ReferralInviteState;

  protected state = ReferralInviteState.Checking;
  protected inviterFirstName = '';

  /**
   * Whether somebody is already signed in on this device.
   *
   * The plan is written around a stranger, but nothing stops an existing user
   * tapping a friend's link — out of curiosity, or because they signed up and
   * came back to it. Sending them to a registration form would be wrong, so the
   * button does something different for them; see `onPrimaryButtonClick`.
   * Read once when the page opens, because signing in from here is not possible.
   */
  protected isAlreadySignedIn = false;

  constructor(
    private navCtrl: NavController,
    private referral: ReferralService,
    private auth: AuthService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter(): Promise<void> {
    this.isAlreadySignedIn = !!this.auth.user;

    const ref = this.referral.readRefFromCurrentUrl();

    if (!ref) {
      this.state = ReferralInviteState.Invalid;
      this.safeMarkForCheck(this.cdRef);
      return;
    }

    // Remember it FIRST, before the server is asked anything, and before the
    // visitor can tap anything. Two things would otherwise lose the invite: the
    // address bar is rewritten without the query string as soon as navigation
    // settles, so a reload of this page would find nothing; and creating an
    // account navigates and reloads, so the value has to already be stored by
    // the time that happens.
    this.referral.rememberForSignUp(ref);

    // deliberately NOT awaited: this hook runs before the page is shown, so
    // awaiting a network call here would leave the visitor looking at a blank
    // screen instead of the "checking your invite" text.
    void this.loadInviteAsync(ref);
  }

  private async loadInviteAsync(ref: string): Promise<void> {
    const result = await this.referral.peekInviteAsync(ref);

    // the visitor may have moved on while the call was in flight
    if (this.viewDestroyed) return;

    // A valid invite carrying no name counts as invalid. Naming the person who
    // sent it is the entire point of this page — the product spec is emphatic
    // that an anonymous invite destroys the effect, because the friend knowing
    // WHO gave it to them is the inviter's whole reward.
    if (result.valid && result.inviterFirstName) {
      this.inviterFirstName = result.inviterFirstName;
      this.state = ReferralInviteState.Valid;
    } else if (result.rateLimited) {
      // the invite may well be valid, so do not call it "not valid"; it is already stored for sign-up
      this.state = ReferralInviteState.TooManyAttempts;
    } else {
      this.state = ReferralInviteState.Invalid;
    }

    this.safeMarkForCheck(this.cdRef);
  }

  /**
   * What the one button on this page does.
   *
   * For the visitor this page is built for — somebody with no account — it opens
   * the registration form. The invite is already in session storage by now, and
   * `AppComponent` claims it once they are signed in.
   *
   * For somebody already signed in there is nothing to register, so it claims
   * the invite here and now and takes them to the home page. The server decides
   * whether the claim is allowed; a refusal is an ordinary answer and is
   * recorded rather than shown, because phase 1 grants nothing, so there is
   * nothing for the user to lose or to act on.
   *
   * `setRootAsync` rather than a push: the visitor arrived here directly, so
   * there is nothing behind this page to go back to.
   */
  protected onPrimaryButtonClick(event: Event): void {
    lockClick(event, this.navigateOnwardsAsync());
  }

  private async navigateOnwardsAsync(): Promise<void> {
    if (!this.isAlreadySignedIn) {
      await this.navCtrl.setRootAsync(ILoginPage, { mode: 'register' });
      return;
    }

    await this.referral.claimPendingIfAnyAsync();
    await this.navCtrl.setRootAsync(IHomePage, {});
  }
}
